import { okdocActionLabels, okdocFeedbackResultCopy } from '@/features/okdoc/config';
import type {
  OkdocScenarioProfile,
  OkdocScenarioResultCopy,
  OkdocScenarioSubmitAction,
  PublicAppointmentActionLanding,
} from '@/features/okdoc/types';

const scenarioTokenPattern = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

const DEFAULT_FEEDBACK_ACTIONS: OkdocScenarioSubmitAction[] = [
  { action: 'positive', label: 'Понравилось' },
  { action: 'neutral', label: 'Все нормально' },
  { action: 'negative', label: 'Не понравилось' },
];

const DEFAULT_APPOINTMENT_ACTIONS: OkdocScenarioSubmitAction[] = [
  { action: 'confirm', label: 'Приду' },
  { action: 'reschedule', label: 'Перенести' },
  { action: 'cancel', label: 'Отменить' },
];

function normalizeValue(value?: string): string {
  return (value || '').trim();
}

function normalizeAction(value?: string): string {
  return normalizeValue(value).toLowerCase();
}

function buildScenarioVariables(landing: PublicAppointmentActionLanding): Record<string, string> {
  return {
    patient_name: landing.patient_name || landing.first_name || '',
    first_name: landing.first_name || '',
    clinic_name: landing.clinic_name || landing.tenant_name || '',
    clinic_phone: landing.clinic_phone || '',
    clinic_website: landing.clinic_website || '',
    clinic_address: landing.clinic_address || '',
    tenant_name: landing.tenant_name || '',
    service_name: landing.appointment?.service_name || '',
    doctor_name: landing.appointment?.doctor_name || '',
    appointment_datetime: landing.appointment?.date_time || '',
  };
}

export function resolveScenarioString(value: string | undefined, variables: Record<string, string>): string {
  if (!value) {
    return '';
  }
  return value.replace(scenarioTokenPattern, (_, rawKey: string) => {
    const key = rawKey.trim().toLowerCase();
    return variables[key] || '';
  });
}

function mergeSubmitActions(
  defaults: OkdocScenarioSubmitAction[],
  overrides: OkdocScenarioSubmitAction[] = [],
): OkdocScenarioSubmitAction[] {
  const overrideMap = new Map(
    overrides
      .map((item) => ({
        action: normalizeAction(item.action),
        label: normalizeValue(item.label),
      }))
      .filter((item) => item.action)
      .map((item) => [item.action, item.label] as const),
  );

  return defaults.map((item) => {
    const action = normalizeAction(item.action);
    const override = overrideMap.get(action);
    return {
      action,
      label: override || normalizeValue(item.label),
    };
  });
}

export function resolveFeedbackSubmitActions(profile?: OkdocScenarioProfile): OkdocScenarioSubmitAction[] {
  return mergeSubmitActions(DEFAULT_FEEDBACK_ACTIONS, profile?.submit_actions || []);
}

export function resolveAppointmentSubmitActions(profile?: OkdocScenarioProfile): OkdocScenarioSubmitAction[] {
  return mergeSubmitActions(DEFAULT_APPOINTMENT_ACTIONS, profile?.submit_actions || []);
}

export function resolveScenarioResultCopy(
  profile: OkdocScenarioProfile | undefined,
  action: string,
): OkdocScenarioResultCopy | null {
  const normalizedAction = normalizeAction(action);
  if (!normalizedAction) {
    return null;
  }

  const match = (profile?.result_copies || []).find((item) => normalizeAction(item.action) === normalizedAction);
  if (!match) {
    return null;
  }

  return {
    action: normalizedAction,
    title: normalizeValue(match.title),
    description: normalizeValue(match.description),
  };
}

export function resolveFeedbackResultCopy(profile: OkdocScenarioProfile | undefined, action: string) {
  const scenarioCopy = resolveScenarioResultCopy(profile, action);
  if (scenarioCopy?.title || scenarioCopy?.description) {
    return {
      title: scenarioCopy.title || '',
      description: scenarioCopy.description || '',
    };
  }
  return okdocFeedbackResultCopy[action] || null;
}

export function hasScenarioHeroCopy(profile?: OkdocScenarioProfile): boolean {
  return Boolean(
    normalizeValue(profile?.badge) ||
      normalizeValue(profile?.title) ||
      normalizeValue(profile?.subtitle) ||
      normalizeValue(profile?.description),
  );
}

export function createDefaultOkdocProfile(flow: string): OkdocScenarioProfile {
  switch (normalizeAction(flow)) {
    case 'feedback_collection':
      return {
        kind: 'feedback_collection',
        shell_title: 'Сбор обратной связи',
        shell_subtitle: 'Ответ автоматически попадёт в систему контроля качества клиники',
        badge: 'Сбор обратной связи',
        title: 'Как прошёл визит?',
        description: 'Нам важно Ваше впечатление после приёма. Выберите вариант, который лучше всего отражает Ваш опыт.',
        submit_actions: DEFAULT_FEEDBACK_ACTIONS,
        result_copies: Object.entries(okdocFeedbackResultCopy).map(([action, value]) => ({
          action,
          title: value.title,
          description: value.description,
        })),
      };
    case 'appointment_confirmation':
      return {
        kind: 'appointment_confirmation',
        shell_title: 'Подтверждение записи',
        shell_subtitle: 'Ответ автоматически попадёт в систему подтверждений клиники',
        submit_actions: DEFAULT_APPOINTMENT_ACTIONS,
      };
    case 'contact_hub':
      return {
        kind: 'contact_hub',
        shell_title: 'Каналы связи и ЛК пациента',
        shell_subtitle: 'Собрали быстрые ссылки для связи с клиникой и полезные сервисы',
        title: 'Остаёмся на связи',
        badge: 'Каналы связи',
        cta_title: 'Каналы связи с нами',
        cta_description: 'Выберите удобный канал связи с клиникой или откройте личный кабинет.',
        variant: 'contact_hub',
        sections: [],
        ctas: [],
      };
    default:
      return {
        kind: 'recommendations',
        shell_title: 'Рекомендации после приёма',
        shell_subtitle: 'Персональные материалы и следующие шаги после визита собраны на одной странице',
        title: 'Рекомендации после приёма',
        badge: 'После приёма',
        cta_title: 'Полезные действия',
        cta_description: 'Откройте персональные материалы и нужные ссылки по Вашему сценарию.',
        variant: 'tenant_custom',
        sections: [],
        ctas: [],
      };
  }
}

export function resolvePublicScenarioProfile(landing: PublicAppointmentActionLanding): OkdocScenarioProfile | undefined {
  const profile = landing.scenario_profile;
  if (!profile) {
    return undefined;
  }

  const variables = buildScenarioVariables(landing);

  return {
    ...profile,
    shell_title: resolveScenarioString(profile.shell_title, variables),
    shell_subtitle: resolveScenarioString(profile.shell_subtitle, variables),
    title: resolveScenarioString(profile.title, variables),
    subtitle: resolveScenarioString(profile.subtitle, variables),
    description: resolveScenarioString(profile.description, variables),
    badge: resolveScenarioString(profile.badge, variables),
    cta_title: resolveScenarioString(profile.cta_title, variables),
    cta_description: resolveScenarioString(profile.cta_description, variables),
    sections: (profile.sections || []).map((section) => ({
      ...section,
      title: resolveScenarioString(section.title, variables),
      description: resolveScenarioString(section.description, variables),
      items: (section.items || []).map((item) => resolveScenarioString(item, variables)),
      entries: (section.entries || []).map((entry) => ({
        ...entry,
        title: resolveScenarioString(entry.title, variables),
        description: resolveScenarioString(entry.description, variables),
        meta: resolveScenarioString(entry.meta, variables),
      })),
      image: section.image
        ? {
            ...section.image,
            url: resolveScenarioString(section.image.url, variables),
            alt: resolveScenarioString(section.image.alt, variables),
            caption: resolveScenarioString(section.image.caption, variables),
          }
        : section.image,
    })),
    ctas: (profile.ctas || []).map((cta) => ({
      ...cta,
      label: resolveScenarioString(cta.label, variables),
      description: resolveScenarioString(cta.description, variables),
      url: resolveScenarioString(cta.url, variables),
      target: resolveScenarioString(cta.target, variables),
    })),
    submit_actions: (profile.submit_actions || []).map((item) => ({
      ...item,
      label: resolveScenarioString(item.label, variables),
    })),
    result_copies: (profile.result_copies || []).map((item) => ({
      ...item,
      title: resolveScenarioString(item.title, variables),
      description: resolveScenarioString(item.description, variables),
    })),
  };
}

export function getOkdocShellCopy(flow: string, profile?: OkdocScenarioProfile): { title: string; subtitle: string } {
  if (normalizeAction(profile?.kind) === 'birthday_offer' || normalizeAction(profile?.kind) === 'appointment_info') {
    return {
      title: profile?.shell_title || '',
      subtitle: profile?.shell_subtitle || '',
    };
  }

  switch (normalizeAction(flow)) {
    case 'feedback_collection':
      return {
        title: profile?.shell_title || 'Сбор обратной связи',
        subtitle: profile?.shell_subtitle || 'Ответ автоматически попадёт в систему контроля качества клиники',
      };
    case 'recommendations':
      if (normalizeAction(profile?.kind) === 'contact_hub') {
        return {
          title: profile?.shell_title || 'Каналы связи и ЛК пациента',
          subtitle: profile?.shell_subtitle || 'Собрали быстрые ссылки для связи с клиникой и полезные сервисы',
        };
      }
      return {
        title: profile?.shell_title || 'Рекомендации после приёма',
        subtitle: profile?.shell_subtitle || 'Персональные материалы и следующие шаги после визита собраны на одной странице',
      };
    case 'external_redirect':
      return {
        title: profile?.shell_title || 'Переход по ссылке',
        subtitle: profile?.shell_subtitle || 'Короткая ссылка подготовлена для безопасного перехода',
      };
    default:
      return {
        title: profile?.shell_title || 'Подтверждение записи',
        subtitle: profile?.shell_subtitle || 'Ответ автоматически попадёт в систему подтверждений клиники',
      };
  }
}

export function getRecommendationsCTACopy(profile?: OkdocScenarioProfile): { title: string; description: string } {
  if (normalizeAction(profile?.kind) === 'birthday_offer') {
    return {
      title: profile?.cta_title || 'Выберите ваш подарок ниже',
      description: profile?.cta_description || '',
    };
  }

  if (normalizeAction(profile?.kind) === 'appointment_info') {
    return {
      title: profile?.cta_title || 'Личный кабинет пациента',
      description: profile?.cta_description || '',
    };
  }

  return {
    title: profile?.cta_title || 'Полезные действия',
    description: profile?.cta_description || 'Откройте персональные материалы и нужные ссылки по Вашему сценарию.',
  };
}

export type OkdocPreviewTenantClinic = {
  clinic_name?: string;
  clinic_phone?: string;
  clinic_website?: string;
  clinic_address?: string;
};

export function buildOkdocPreviewLanding(
  flow: string,
  tenantName: string,
  tenantSlug: string | undefined,
  scenarioProfile?: OkdocScenarioProfile,
  tenantClinic?: OkdocPreviewTenantClinic,
): PublicAppointmentActionLanding {
  const displayClinicName = (tenantClinic?.clinic_name || tenantName || '').trim() || 'Клиника';
  return {
    status: 'ready',
    code: 'preview',
    flow,
    scenario_kind: scenarioProfile?.kind || flow,
    scenario_profile: scenarioProfile,
    tenant_name: tenantName || 'Клиника',
    tenant_slug: tenantSlug,
    clinic_name: displayClinicName,
    clinic_phone: (tenantClinic?.clinic_phone || '').trim(),
    clinic_website: (tenantClinic?.clinic_website || '').trim(),
    clinic_address: (tenantClinic?.clinic_address || '').trim(),
    patient_name: 'Тестовый пациент',
    first_name: 'Пациент',
    review_url: '',
    review_label: 'Оставить отзыв',
    appointment: {
      date_time: '02.04.2026 15:30',
      doctor_name: 'Иван Иванов',
      service_name: 'Консультация',
    },
  };
}

export function getAppointmentActionLabel(action: string): string {
  return okdocActionLabels[action] || action;
}
